import React, { useState, useEffect } from "react";
import Popup from "components/pop-up/pop-up";
import { useTooltip } from "context/TooltipProvider";
import {
  createAssetProvision,
  updateAssetSeizureRestraint,
} from "services/assetRecoveryService";
import { Asset, SeizureRestraintFormData } from "types";
import { MultipleFileUploadComponent } from "components/form/MultipleFileUpload";
import { useTranslation } from "react-i18next";

interface SeizureRestraintFormProps {
  isOpen: boolean;
  onClose: () => void;
  referralId: string;
  asset: Asset | null; // The specific asset to apply seizure/restraint to
  onSuccess: (data: any) => void;
  mode: "create" | "edit";
  editData?: any; // Existing seizure/restraint data for edit mode
  editId?: string; // ID of the specific seizure/restraint record
  onEditComplete?: () => void;
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
}

const SEIZURE_TYPE_CHOICES = [
  { value: "SEIZURE", label: "Seizure" },
  { value: "RESTRAIN", label: "Restrain" },
];

const STATUS_CHOICES = [
  { value: "PENDING", label: "Pending" },
  { value: "UNDER_SUPERVISION", label: "Under Supervision" },
  { value: "RELEASED", label: "Released" },
  { value: "CONFISCATED", label: "Confiscated" },
  { value: "TRANSFERRED", label: "Transferred" },
  { value: "CANCELLED", label: "Cancelled" },
];

const SeizureRestraintForm: React.FC<SeizureRestraintFormProps> = ({
  isOpen,
  onClose,
  referralId,
  asset,
  onSuccess,
  mode = "create",
  editData,
  editId,
  onEditComplete,
  setActiveTab,
}) => {
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState<SeizureRestraintFormData>({
    seizure_type: null,
    supervising_authority: "",
    supervising_name: "",
    supervision_notes: "",
    status: null,
    release_date: null,
    release_reason: "",
    asset: asset?.id || null,
  });

  const isEditMode = mode === "edit" && editData && editId;
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  // Initialize form with edit data if in edit mode
  useEffect(() => {
    if (isOpen) {
      if (isEditMode && editData) {
        console.log(
          "Initializing edit mode for seizure/restraint:",
          editId,
          editData,
        );
        setFormData({
          seizure_type: editData.seizure_type || null,
          supervising_authority: editData.supervising_authority || "",
          supervising_name: editData.supervising_name || "",
          supervision_notes: editData.supervision_notes || "",
          status: editData.status || null,
          release_date: editData.release_date
            ? editData.release_date.split("T")[0]
            : null,
          release_reason: editData.release_reason || "",
          asset: editData.asset || asset?.id || null,
        });
      } else {
        // Reset form for create mode
        resetForm();
      }
    }
  }, [isOpen, mode, editData, editId, asset]);

  const resetForm = () => {
    setFormData({
      seizure_type: null,
      supervising_authority: "",
      supervising_name: "",
      supervision_notes: "",
      status: null,
      release_date: null,
      release_reason: "",
      asset: asset?.id || null,
    });
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value === "" ? null : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.seizure_type) {
      showTooltip(
        t("seizure_type_required", {
          defaultValue: "Please select seizure type",
        }),
        "error",
      );
      return;
    }

    if (!formData.supervising_authority.trim()) {
      showTooltip(
        t("supervising_authority_required", {
          defaultValue: "Supervising authority is required",
        }),
        "error",
      );
      return;
    }

    if (!formData.supervising_name.trim()) {
      showTooltip(
        t("supervising_name_required", {
          defaultValue: "Supervising name is required",
        }),
        "error",
      );
      return;
    }

    if (!formData.status) {
      showTooltip(
        t("status_required", {
          defaultValue: "Please select status",
        }),
        "error",
      );
      return;
    }

    // Validate release fields if status is RELEASED
    if (formData.status === "RELEASED") {
      if (!formData.release_date) {
        showTooltip(
          "Release date is required when status is Released",
          "error",
        );
        return;
      }
      if (!formData.release_reason.trim()) {
        showTooltip(
          "Release reason is required when status is Released",
          "error",
        );
        return;
      }
    }

    if (!formData.asset) {
      showTooltip(
        t("asset_information_missing", {
          defaultValue: "Asset information is missing",
        }),
        "error",
      );
      return;
    }

    setLoading(true);

    try {
      let response;

      // Prepare submission data
      const submissionData = {
        ...formData,
        // Clear release fields if status is not RELEASED
        release_date:
          formData.status === "RELEASED" ? formData.release_date : null,
        release_reason:
          formData.status === "RELEASED" ? formData.release_reason : "",
        asset_provision_documents: uploadedFiles,
      };

      if (isEditMode && editId) {
        // Update existing seizure/restraint
        response = await updateAssetSeizureRestraint(editId, submissionData);
        showTooltip(
          t("seizure_restraint_updated_successfully", {
            defaultValue: "Seizure/Restraint updated successfully!",
          }),
          "success",
        );
      } else {
        // Create new seizure/restraint
        response = await createAssetProvision(submissionData);
        showTooltip(
          t("seizure_restraint_created_successfully", {
            defaultValue: "Seizure/Restraint created successfully!",
          }),
          "success",
        );
      }

      onClose();
      resetForm();

      if (onSuccess) {
        onSuccess(response);
        setActiveTab?.("Referral");
      }

      if (isEditMode && onEditComplete) {
        onEditComplete();
      }
    } catch (error) {
      console.error("Error submitting seizure/restraint:", error);
      showTooltip(
        isEditMode
          ? t("failed_to_update_seizure_restraint", {
              defaultValue: "Failed to update seizure/restraint",
            })
          : t("failed_to_create_seizure_restraint", {
              defaultValue: "Failed to create seizure/restraint",
            }),
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Popup isOpen={isOpen}>
      <div
        className="referral-details-popup-container"
        style={{ maxWidth: "650px" }}
      >
        <div className="pop-up-form">
          <div className="form-header flex justify-between items-center">
            <div>
              <p className="referral-popup-title">
                {isEditMode
                  ? "Edit Seizure/Restraint"
                  : "Add Seizure/Restraint"}
              </p>
              <p className="description">
                {isEditMode
                  ? "Update the seizure or restraint details for this asset"
                  : "Apply seizure or restraint to an asset"}
              </p>
              {asset && (
                <p className="text-xs text-gray-500 mt-1">
                  Asset:{" "}
                  {asset.asset_number || asset.asset_unique_id || asset.id}
                </p>
              )}
              {isEditMode && editId && (
                <p className="text-xs text-gray-500 mt-1">
                  Record ID: {editId}
                </p>
              )}
            </div>

            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              onClick={handleClose}
              className="cursor-pointer"
            >
              <path
                d="M16.95 8.46404C17.1322 8.27544 17.233 8.02283 17.2307 7.76064C17.2284 7.49844 17.1232 7.24763 16.9378 7.06222C16.7524 6.87681 16.5016 6.77164 16.2394 6.76936C15.9772 6.76709 15.7246 6.86788 15.536 7.05004L12 10.586L8.464 7.05004C8.37175 6.95453 8.26141 6.87835 8.13941 6.82594C8.0174 6.77353 7.88618 6.74594 7.7534 6.74479C7.62062 6.74363 7.48894 6.76894 7.36605 6.81922C7.24315 6.8695 7.1315 6.94375 7.03761 7.03764C6.94371 7.13154 6.86946 7.24319 6.81918 7.36609C6.7689 7.48898 6.7436 7.62066 6.74475 7.75344C6.7459 7.88622 6.77349 8.01744 6.8259 8.13944C6.87831 8.26145 6.95449 8.37179 7.05 8.46404L10.586 12L7.05 15.536C6.95449 15.6283 6.87831 15.7386 6.8259 15.8606C6.77349 15.9826 6.7459 16.1139 6.74475 16.2466C6.7436 16.3794 6.7689 16.5111 6.81918 16.634C6.86946 16.7569 6.94371 16.8685 7.03761 16.9624C7.1315 17.0563 7.24315 17.1306 7.36605 17.1809C7.48894 17.2311 7.62062 17.2564 7.7534 17.2553C7.88618 17.2541 8.0174 17.2265 8.13941 17.1741C8.26141 17.1217 8.37175 17.0456 8.464 16.95L12 13.414L15.536 16.95C15.6282 17.0456 15.7386 17.1217 15.8606 17.1741C15.9826 17.2265 16.1138 17.2541 16.2466 17.2553C16.3794 17.2564 16.5111 17.2311 16.634 17.1809C16.7569 17.1306 16.8685 17.0563 16.9624 16.9624C17.0563 16.8685 17.1305 16.7569 17.1808 16.634C17.2311 16.5111 17.2564 16.3794 17.2553 16.2466C17.2541 16.1139 17.2265 15.9826 17.1741 15.8606C17.1217 15.7386 17.0455 15.6283 16.95 15.536L13.414 12L16.95 8.46404Z"
                fill="black"
              />
            </svg>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Seizure Type Selection */}
            <div className="flex flex-col mt-4">
              <label className="referral-popup-label">
                Seizure Type <span className="text-red-500">*</span>
              </label>
              <select
                className="referral-form-field"
                name="seizure_type"
                value={formData.seizure_type || ""}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Seizure Type</option>
                {SEIZURE_TYPE_CHOICES.map((choice) => (
                  <option key={choice.value} value={choice.value}>
                    {choice.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Supervising Authority */}
            <div className="flex flex-col mt-4">
              <label className="referral-popup-label">
                Supervising Authority <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="referral-form-field"
                name="supervising_authority"
                placeholder="Enter supervising authority"
                value={formData.supervising_authority}
                onChange={handleInputChange}
                required
              />
            </div>

            {/* Supervising Name */}
            <div className="flex flex-col mt-4">
              <label className="referral-popup-label">
                Supervising Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="referral-form-field"
                name="supervising_name"
                placeholder="Enter supervising person's name"
                value={formData.supervising_name}
                onChange={handleInputChange}
                required
              />
            </div>

            {/* Supervision Notes */}
            <div className="flex flex-col mt-4">
              <label className="referral-popup-label">Supervision Notes</label>
              <textarea
                className="referral-form-field h-24"
                name="supervision_notes"
                placeholder="Enter supervision notes"
                value={formData.supervision_notes}
                onChange={handleInputChange}
              />
            </div>

            {/* Status Selection */}
            <div className="flex flex-col mt-4">
              <label className="referral-popup-label">
                Status <span className="text-red-500">*</span>
              </label>
              <select
                className="referral-form-field"
                name="status"
                value={formData.status || ""}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Status</option>
                {STATUS_CHOICES.map((choice) => (
                  <option key={choice.value} value={choice.value}>
                    {choice.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Release Fields - Only show when status is RELEASED */}
            {formData.status === "RELEASED" && (
              <>
                <div className="flex flex-col mt-4">
                  <label className="referral-popup-label">
                    Release Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    className="referral-form-field"
                    name="release_date"
                    value={formData.release_date || ""}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="flex flex-col mt-4">
                  <label className="referral-popup-label">
                    Release Reason <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    className="referral-form-field h-24"
                    name="release_reason"
                    placeholder="Enter reason for release"
                    value={formData.release_reason}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </>
            )}
            <MultipleFileUploadComponent
              onFilesChange={setUploadedFiles}
              uploadedFiles={uploadedFiles}
              label="Upload provision file"
            />

            {/* Hidden asset field */}
            <input type="hidden" name="asset" value={formData.asset || ""} />

            {/* Submit Button */}
            <div className="mt-6 mb-4">
              <button
                type="submit"
                disabled={loading}
                className={`referral-form-button text-white w-full ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {loading
                  ? "Submitting..."
                  : isEditMode
                    ? "Update Provision"
                    : "Create Provision"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Popup>
  );
};

export default SeizureRestraintForm;
